/**
 * Number validation utilities for type-safe number conversions and validations.
 * Used primarily for feature flag values that come as unknown types.
 */

/**
 * Converts an unknown value to a number.
 * Handles strings, numbers, null, undefined, objects, and arrays.
 *
 * @param value - The value to convert
 * @returns The numeric value or NaN if conversion is not possible
 *
 * @example
 * toNumber('123') // 123
 * toNumber(456) // 456
 * toNumber('12.5') // 12.5
 * toNumber('not a number') // NaN
 * toNumber(null) // NaN
 * toNumber({}) // NaN
 */
export const toNumber = (value: unknown): number => {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') return NaN;

  // Reject objects and arrays (but allow numbers and strings)
  if (typeof value === 'object') return NaN;

  // Convert string to number using parseFloat, or cast non-string primitives to Number
  return typeof value === 'string' ? parseFloat(value) : Number(value);
};

/**
 * Checks if a value can be converted to a valid numeric value.
 * Returns false for NaN, null, undefined, objects, arrays, and non-numeric strings.
 *
 * @param value - The value to check
 * @returns true if the value is numeric, false otherwise
 *
 * @example
 * isNumeric('123') // true
 * isNumeric(456) // true
 * isNumeric('12.5') // true
 * isNumeric('not a number') // false
 * isNumeric(null) // false
 * isNumeric(NaN) // false
 */
export const isNumeric = (value: unknown): boolean => {
  const num = toNumber(value);
  return !isNaN(num);
};

/**
 * Checks if a number is positive (greater than zero).
 *
 * @param value - The number to check
 * @returns true if the number is positive, false otherwise
 *
 * @example
 * isPositiveNumber(10) // true
 * isPositiveNumber(0) // false
 * isPositiveNumber(-5) // false
 */
export const isPositiveNumber = (value: number): boolean => {
  return value > 0;
};

/**
 * Validates and converts an unknown value to a positive number.
 * Returns 0 if the value is not a valid positive number.
 *
 * This is a composition of toNumber, isNumeric, and isPositiveNumber utilities.
 * Useful for validating feature flag values or user inputs that should be positive numbers.
 *
 * @param value - The value to validate and convert
 * @returns The positive number or 0 if invalid
 *
 * @example
 * validatePositiveNumber('100') // 100
 * validatePositiveNumber(50) // 50
 * validatePositiveNumber('12.5') // 12.5
 * validatePositiveNumber('0') // 0
 * validatePositiveNumber('-10') // 0
 * validatePositiveNumber('not a number') // 0
 * validatePositiveNumber(null) // 0
 * validatePositiveNumber({}) // 0
 */
export const validatePositiveNumber = (value: unknown): number => {
  if (!isNumeric(value)) return 0;

  const numValue = toNumber(value);
  return isPositiveNumber(numValue) ? numValue : 0;
};
